import { useCallback, useEffect, useRef, useState } from 'react';

export interface Page<T, M = undefined> {
  rows: T[];
  total: number;
  /** Extra data that only the first page carries (e.g. search's set matches). */
  meta?: M;
}

export type FetchPage<T, M = undefined> = (offset: number, limit: number, signal?: AbortSignal) => Promise<Page<T, M>>;

const PAGE_SIZE = 60;
const CACHE_TTL = 5 * 60 * 1000;
// Survives unmount so Back to a long list renders the same rows instantly.
const listCache = new Map<string, { rows: unknown[]; total: number; meta: unknown; at: number }>();

/** Drop every cached list; call after a mutation so Back/revisits refetch. */
export const clearPagedListCache = () => listCache.clear();

/**
 * Infinite-scroll list over any offset/limit endpoint. A new `key` (the
 * serialised query) starts over at page one; attach sentinelRef to an element
 * below the list and the next page loads as it nears the viewport.
 */
export function usePagedList<T extends { id: string }, M = undefined>(
  fetchPage: FetchPage<T, M>,
  key: string,
  enabled = true
) {
  const initial = useRef<{ rows: T[]; total: number; meta: M | undefined } | null>(null);
  if (initial.current === null) {
    const hit = enabled ? listCache.get(key) : undefined;
    initial.current =
      hit && Date.now() - hit.at < CACHE_TTL
        ? { rows: hit.rows as T[], total: hit.total, meta: hit.meta as M | undefined }
        : { rows: [], total: 0, meta: undefined };
  }
  const restored = useRef(initial.current.rows.length > 0);
  // The key the shown rows belong to, so stale rows never get cached under a new key.
  const rowsKey = useRef<string | null>(restored.current ? key : null);
  const [rows, setRows] = useState<T[]>(initial.current.rows);
  const [total, setTotal] = useState(initial.current.total);
  const [meta, setMeta] = useState<M | undefined>(initial.current.meta);
  const [status, setStatus] = useState<'idle' | 'loading' | 'ready' | 'error'>(
    restored.current ? 'ready' : 'idle'
  );
  const [error, setError] = useState<unknown>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  // fetchPage is usually an inline closure; only `key` decides when to reset.
  const fetchRef = useRef(fetchPage);
  fetchRef.current = fetchPage;
  // Bumped on every reset so a page that lands after the query changed is dropped.
  const generation = useRef(0);
  const rowsRef = useRef<T[]>([]);
  rowsRef.current = rows;
  const totalRef = useRef(0);
  totalRef.current = total;
  const busy = useRef(false);

  const metaRef = useRef(meta);
  metaRef.current = meta;
  useEffect(() => {
    if (enabled && status === 'ready' && rowsKey.current === key) listCache.set(key, { rows, total, meta: metaRef.current, at: Date.now() });
  }, [key, enabled, status, rows, total]);

  useEffect(() => {
    const gen = ++generation.current;
    if (restored.current) {
      // First run after a cache hit: keep the restored rows, skip the fetch.
      restored.current = false;
      return;
    }
    rowsKey.current = null;
    setRows([]);
    setTotal(0);
    setMeta(undefined);
    if (!enabled) {
      setStatus('idle');
      return;
    }
    setStatus('loading');
    busy.current = true;
    const controller = new AbortController();
    fetchRef
      .current(0, PAGE_SIZE, controller.signal)
      .then((page) => {
        if (gen !== generation.current) return;
        rowsKey.current = key;
        setRows(page.rows);
        setTotal(page.total);
        setMeta(page.meta);
        setStatus('ready');
      })
      .catch((err) => {
        if (gen !== generation.current) return;
        setError(err);
        setStatus('error');
      })
      .finally(() => {
        if (gen === generation.current) busy.current = false;
      });
    return () => controller.abort();
  }, [key, enabled, reloadKey]);

  const loadMore = useCallback(() => {
    if (busy.current || rowsRef.current.length >= totalRef.current) return;
    const gen = generation.current;
    busy.current = true;
    setLoadingMore(true);
    fetchRef
      .current(rowsRef.current.length, PAGE_SIZE)
      .then((page) => {
        if (gen !== generation.current) return;
        setRows((prev) => {
          // Rows removed locally (e.g. quantity hit 0) shift later pages left;
          // skip anything already shown rather than rendering a duplicate key.
          const seen = new Set(prev.map((r) => r.id));
          return [...prev, ...page.rows.filter((r) => !seen.has(r.id))];
        });
        setTotal(page.total);
      })
      .catch(() => {
        /* the sentinel retries on its next intersection */
      })
      .finally(() => {
        if (gen === generation.current) {
          busy.current = false;
          setLoadingMore(false);
        }
      });
  }, []);

  // Re-observing whenever the row count changes makes the observer report the
  // sentinel's current state, so a page too short to push it off-screen (or a
  // client-side filter hiding most rows) still pulls the next one.
  const observer = useRef<IntersectionObserver | null>(null);
  const shown = rows.length;
  const sentinelRef = useCallback(
    (node: HTMLElement | null) => {
      observer.current?.disconnect();
      if (!node) return;
      observer.current = new IntersectionObserver(
        (entries) => {
          if (entries.some((e) => e.isIntersecting)) loadMore();
        },
        { rootMargin: '800px 0px' }
      );
      observer.current.observe(node);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [loadMore, shown]
  );

  // Re-read everything shown so far in one request, keeping the scroll
  // position (for callers that only learn "something changed").
  const refresh = useCallback(async () => {
    const gen = generation.current;
    const page = await fetchRef.current(0, Math.max(rowsRef.current.length, PAGE_SIZE));
    if (gen !== generation.current) return;
    setRows(page.rows);
    setTotal(page.total);
  }, []);

  return {
    rows,
    setRows,
    total,
    setTotal,
    meta,
    status,
    error,
    loadingMore,
    hasMore: rows.length < total,
    loadMore,
    sentinelRef,
    refresh,
    reload: useCallback(() => setReloadKey((k) => k + 1), []),
  };
}
